import React, { Component, ErrorInfo, ReactNode } from "react";
import { track } from "../analytics";
import { EVENTS } from "../analytics/events";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  reloading: boolean;
  error?: Error;
  info?: ErrorInfo;
}

/**
 * A code-split / dynamic-import chunk failed to load — almost always because a new build
 * replaced the chunk hashes the running app still references (after a deploy or a dev rebuild).
 * Navigating to a lazy route then 404s its chunk. The fix is just to reload and fetch the fresh
 * bundle, which is exactly the "refresh and it works" behavior users hit manually.
 */
function isChunkLoadError(error?: Error): boolean {
  if (!error) return false;
  const name = error.name || "";
  const msg = error.message || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /failed to (fetch|import) dynamically imported module/i.test(msg)
  );
}

const CHUNK_RELOAD_KEY = "chunkReloadAt";

// Show the underlying error on screen outside production so a "Something went wrong" is
// actually diagnosable (the d1/dev tier is not production).
const SHOW_DETAILS = process.env.NODE_ENV !== "production";

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, reloading: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Stale-chunk errors: auto-reload once to pick up the fresh bundle instead of showing the
    // error screen. Guard against reload loops (a chunk that 404s permanently) by only reloading
    // if we haven't already done so in the last 10s.
    if (isChunkLoadError(error)) {
      const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        window.location.reload();
        return { hasError: false, reloading: true };
      }
    }
    return { hasError: true, reloading: false, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, info: errorInfo });
    if (!isChunkLoadError(error)) {
      console.error("Uncaught error:", error, errorInfo);
    }
    // A white screen is the worst thing this app can do, so it gets counted. The
    // error's CLASS name only — `message` and `stack` routinely quote record values
    // and file paths, and neither belongs in GA (ANALYTICS.md §9).
    try {
      track(EVENTS.ERROR_SHOWN, {
        error_scope: isChunkLoadError(error) ? "stale_chunk" : "react_render",
        error_code: error?.name || "Error",
      });
    } catch {
      // Never let reporting a crash cause a second one.
    }
  }

  render() {
    if (this.state.reloading) {
      return null; // reloading to fetch the fresh bundle
    }

    if (this.state.hasError) {
      const { error, info } = this.state;
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h1>Something went wrong.</h1>
          <p>Please try refreshing the page.</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: "0.5rem 1rem", cursor: "pointer" }}
          >
            Refresh Page
          </button>

          {SHOW_DETAILS && error && (
            <details open style={{ marginTop: "1.5rem", textAlign: "left" }}>
              <summary style={{ cursor: "pointer", color: "#b00020", fontWeight: 600 }}>
                {error.name}: {error.message}
              </summary>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  overflow: "auto",
                  maxHeight: 360,
                  background: "#f6f6f6",
                  padding: "1rem",
                  borderRadius: 6,
                  fontSize: 12,
                  marginTop: 8,
                }}
              >
                {error.stack}
                {info?.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
